import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import EditCompetiteurModal from "./../CompetitionJudokasPage/EditCompetiteurModal";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import Button from "./../../components/Button/Button";
import UploadJudokasModal from "../CompetitionJudokasPage/UploadJudokasModal"; // ⇐ réutilisée

import {
  getCompetitionCompetiteurs,
  addCompetiteur,
  deleteCompetiteur,
} from "../../services/competiteursServices";
import { getCompetitionById } from "../../services/competitionsServices";
import { getCompetitionSettingsByCompetitionId } from "../../services/competitionsettingsServices";

/* ====== Constantes partagées (même logique que les modales) ====== */
const ALL_DISCIPLINES = [
  "Judo",
  "Ne-Waza",
  "JJB",
  "Jujitsu Combat",
  "Jujitsu Duo",
  "Arbitre",
  "Commissaire Sportif",
  "Judo Adapté"

];

const GRADE_OPTIONS = [
  { label: "Ceinture blanche", value: "blanche" },
  { label: "Ceinture blanche 1 liseré", value: "blanche 1 liseré" },
  { label: "Ceinture blanche 2 liserés", value: "blanche 2 liserés" },
  { label: "Ceinture blanche-jaune", value: "blanche-jaune" },
  { label: "Ceinture jaune", value: "jaune" },
  { label: "Ceinture jaune-orange", value: "jaune-orange" },
  { label: "Ceinture orange", value: "orange" },
  { label: "Ceinture orange-verte", value: "orange-verte" },
  { label: "Ceinture verte", value: "verte" },
  { label: "Ceinture verte-bleue", value: "verte-bleue" },
  { label: "Ceinture bleue", value: "bleue" },
  { label: "Ceinture bleue-marron", value: "bleue-marron" },
  { label: "Ceinture marron", value: "marron" },
  { label: "1er Dan", value: "1er Dan" },
  { label: "2e Dan", value: "2e Dan" },
  { label: "3e Dan", value: "3e Dan" },
  { label: "4e Dan", value: "4e Dan" },
  { label: "5e Dan", value: "5e Dan" },
];

const normalize = (str) =>
  String(str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const getGradeClasses = (g = "") => {
  const s = normalize(g);
  if (s.includes("blanche 2 liseres"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche 1 lisere"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche-jaune"))
    return "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";

  if (s.includes("jaune-orange"))
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";
  if (s.includes("jaune"))
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";

  if (s.includes("orange-verte"))
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 text-xs";
  if (s.includes("orange"))
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 text-xs";

  if (s.includes("verte-bleue"))
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 text-xs";
  if (s.includes("verte"))
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 text-xs";

  if (s.includes("bleue-marron"))
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 text-xs";
  if (s.includes("bleue"))
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 text-xs";

  if (s.includes("marron") || s.includes("brun"))
    return "bg-amber-900 text-amber-100 dark:bg-amber-900/40 dark:text-amber-100 text-xs";

  if (s.includes("dan"))
    return "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-xs";

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
};

const coerceGradeValue = (raw = "") => {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (/^ceinture\s+/i.test(trimmed))
    return trimmed.replace(/^ceinture\s+/i, "");
  return trimmed;
};

const labelFromValue = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "Aucun grade";
  return GRADE_OPTIONS.find((o) => o.value === v)?.label || v;
};

/* Petit tag genre, cohérent avec la page Pesées */
const GenreTag = ({ g }) => {
  if (g === "F")
    return (
      <span className="inline-flex items-center rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200 px-2 py-0.5 text-xs font-semibold">
        F
      </span>
    );
  if (g === "M")
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 px-2 py-0.5 text-xs font-semibold">
        M
      </span>
    );
  return <span className="text-xs text-gray-500">—</span>;
};

const WeightBadge = ({ w }) => {
  const val = Number(w);
  if (!w || isNaN(val) || val <= 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 px-2 py-0.5 text-xs">
        Non pesé(e)
      </span>
    );
  }
  // Affiche toujours 1 décimale (ex: 24.0)
  const txt = val.toFixed(1);
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5 text-xs font-bold">
      {txt}
    </span>
  );
};

const CompetitionInvitationPage = () => {
  useScrollToTop();
  const { competitionId } = useParams();
  const [searchParams] = useSearchParams();
  const clubFromUrl = searchParams.get("club") || "";

  const [competition, setCompetition] = useState(null);
  const [settings, setSettings] = useState(null);
  const [competiteurs, setCompetiteurs] = useState([]);
  const [totalCompetitionCount, setTotalCompetitionCount] = useState(0);
  const [availableDisciplines, setAvailableDisciplines] = useState(["Judo"]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [competiteurToEdit, setCompetiteurToEdit] = useState(null);
  const [search, setSearch] = useState("");

  // ⇩⇩ Ajout import modale
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [newCompetiteur, setNewCompetiteur] = useState({
    nom_competiteur: "",
    prenom_competiteur: "",
    ddn_competiteur: "",
    licence_competiteur: "",
    genre_competiteur: "M",
    poids_competiteur: "",
    club_competiteur: clubFromUrl,
    discipline_competiteur: "Judo",
    grade_competiteur: "", // nouveau : grade
  });

  /* ====== Data ====== */
  useEffect(() => {
    const fetchCompetitionAndSettings = async () => {
      try {
        const data = await getCompetitionById(competitionId);
        setCompetition(data);

        // Disciplines autorisées
        let parsed = ["Judo"];
        try {
          parsed = Array.isArray(data.disciplines_competition)
            ? data.disciplines_competition
            : JSON.parse(data.disciplines_competition || '["Judo"]');
        } catch {
          parsed = ["Judo"];
        }
        const finalList = parsed.filter((d) => ALL_DISCIPLINES.includes(d));
        setAvailableDisciplines(finalList.length ? finalList : ["Judo"]);
        setNewCompetiteur((prev) => ({
          ...prev,
          discipline_competiteur: (finalList.length ? finalList : ["Judo"])[0],
        }));
      } catch {
        toast.error("Erreur lors du chargement de la compétition.");
      }

      // Settings (dont quota_competition)
      try {
        const s = await getCompetitionSettingsByCompetitionId(competitionId);
        if (Array.isArray(s) && s.length > 0) {
          setSettings(s[0]);
        }
      } catch {
        // on ne bloque pas si les settings ne répondent pas
      }
    };

    fetchCompetitionAndSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  const fetchCompetiteurs = async (club) => {
    try {
      const all = await getCompetitionCompetiteurs(competitionId);

      // total global pour la jauge
      setTotalCompetitionCount(Array.isArray(all) ? all.length : 0);
      const rows = (all || []).filter(
        (c) =>
          c.club_competiteur?.trim().toLowerCase() === club.trim().toLowerCase()
      );
      setCompetiteurs(rows);

      return all; // permet de connaître le total après ajout
    } catch {
      toast.error("Erreur lors du chargement des compétiteurs.");
      return null;
    }
  };

  useEffect(() => {
    if (clubFromUrl.trim().length > 2) {
      fetchCompetiteurs(clubFromUrl);
    }
  }, [clubFromUrl]);

  /* ====== Handlers ====== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCompetiteur((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async () => {
    // contrôles simples
    if (!newCompetiteur.nom_competiteur || !newCompetiteur.prenom_competiteur) {
      toast.error("Nom et Prénom sont requis.");
      return;
    }
    if (!newCompetiteur.ddn_competiteur) {
      toast.error("Date de naissance requise.");
      return;
    }
    if (!newCompetiteur.genre_competiteur) {
      toast.error("Genre requis.");
      return;
    }

    const poids =
      newCompetiteur.poids_competiteur === "" ||
      newCompetiteur.poids_competiteur === null
        ? null
        : Number(newCompetiteur.poids_competiteur);

    if (poids !== null && (isNaN(poids) || poids < 0 || poids > 300)) {
      toast.error("Poids invalide (0–300 kg).");
      return;
    }

    try {
      const payload = {
        ...newCompetiteur,
        poids_competiteur: poids,
        competition_id: competitionId,
        grade_competiteur:
          coerceGradeValue(newCompetiteur.grade_competiteur) || null,
      };

      await addCompetiteur(payload);

      // On recharge la liste pour récupérer le vrai total global
      const allAfter = await fetchCompetiteurs(newCompetiteur.club_competiteur);
      toast.success("Compétiteur ajouté.");

      // ⚠️ Info quota / liste d'attente
      if (quotaInfo.hasQuota && Array.isArray(allAfter)) {
        const quota = quotaInfo.quota;
        const total = allAfter.length;

        if (total > quota) {
          toast.warn(
            `Le quota global de ${quota} compétiteurs est déjà dépassé. Ce compétiteur sera probablement en liste d'attente. Merci de contacter le club organisateur pour confirmation.`
          );
        } else if (total === quota) {
          toast.info(
            `Vous venez d'atteindre le quota maximum de ${quota} compétiteurs pour cette compétition. Les suivants seront potentiellement en liste d'attente.`
          );
        }
      }

      setNewCompetiteur((prev) => ({
        nom_competiteur: "",
        prenom_competiteur: "",
        ddn_competiteur: "",
        licence_competiteur: "",
        genre_competiteur: prev.genre_competiteur || "M",
        poids_competiteur: "",
        club_competiteur: prev.club_competiteur, // conserve le club
        discipline_competiteur: prev.discipline_competiteur,
        grade_competiteur: "",
      }));
    } catch {
      toast.error("Erreur lors de l'ajout du compétiteur.");
    }
  };

  const handleEdit = (competiteur) => {
    setCompetiteurToEdit(competiteur);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id, is_present) => {
    if (is_present) return;
    if (!window.confirm("Supprimer ce compétiteur ?")) return;
    try {
      await deleteCompetiteur(id);
      await fetchCompetiteurs(newCompetiteur.club_competiteur);
      toast.success("Compétiteur supprimé.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const quotaInfo = useMemo(() => {
    const raw =
      settings?.quota_competition ?? competition?.quota_competition ?? null;

    const quota = Number(raw);
    if (!Number.isFinite(quota) || quota <= 0) {
      return {
        hasQuota: false,
        quota: null,
        total: totalCompetitionCount,
        remaining: null,
        ratio: 0,
        isFull: false,
        isClose: false,
      };
    }

    const total = totalCompetitionCount;
    const remaining = Math.max(0, quota - total);
    const ratio = Math.min(1, total / quota);
    const isFull = total >= quota;
    const isClose = !isFull && ratio >= 0.8;

    return {
      hasQuota: true,
      quota,
      total,
      remaining,
      ratio,
      isFull,
      isClose,
    };
  }, [settings, competition, totalCompetitionCount]);

  /* ====== Vue filtrée côté client ====== */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return competiteurs;
    return competiteurs.filter((c) =>
      [c.nom_competiteur, c.prenom_competiteur, c.licence_competiteur]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [competiteurs, search]);

  const tdClass =
    "px-4 py-2 border-b border-gray-100 dark:border-slate-700/60 align-middle";

  return (
    <div className="flex flex-col w-full mx-auto p-3 mt-[130px] dark:text-white">
      <ScrollIndicator />

      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Inscriptions — Club
        </h2>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          {competition?.nom_competition || "Compétition"}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {competition?.date_competition &&
            new Date(competition.date_competition).toLocaleDateString("fr-FR")}
        </p>
        {clubFromUrl && (
          <p className="mt-2 text-base text-gray-700 dark:text-gray-300">
            Club : <strong>{clubFromUrl}</strong>
          </p>
        )}
      </div>

      {quotaInfo.hasQuota && (
        <div className="mx-auto w-full max-w-4xl mb-6 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/80 backdrop-blur shadow-sm px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">
                  Jauge globale
                </span>{" "}
                — {quotaInfo.total} inscrit
                {quotaInfo.total > 1 ? "s" : ""} / {quotaInfo.quota}
              </p>
              <p
                className={`mt-1 text-xs sm:text-sm ${
                  quotaInfo.isFull
                    ? "text-red-700 dark:text-red-300"
                    : quotaInfo.isClose
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-slate-600 dark:text-slate-300"
                }`}
              >
                {quotaInfo.isFull ? (
                  <>
                    ⚠ Le quota maximum est atteint. Vous pouvez encore inscrire
                    des compétiteurs, mais ils seront considérés comme{" "}
                    <span className="font-semibold">
                      sur liste d&apos;attente
                    </span>
                    . Merci de{" "}
                    <span className="font-semibold">
                      contacter le club organisateur
                    </span>{" "}
                    pour toute confirmation.
                  </>
                ) : (
                  <>
                    Il reste{" "}
                    <span className="font-semibold">
                      {quotaInfo.remaining} place
                      {quotaInfo.remaining > 1 ? "s" : ""}
                    </span>{" "}
                    avant d&apos;atteindre le quota fixé par
                    l&apos;organisation.
                  </>
                )}
              </p>
            </div>

            <div className="w-full sm:w-56">
              <div className="h-3 rounded-full bg-slate-200/80 dark:bg-slate-800/80 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    quotaInfo.isFull
                      ? "bg-red-500"
                      : quotaInfo.isClose
                      ? "bg-amber-400"
                      : "bg-emerald-500"
                  }`}
                  style={{ width: `${quotaInfo.ratio * 100}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-right text-slate-500 dark:text-slate-400">
                {Math.round(quotaInfo.ratio * 100)}% du quota
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire d'ajout + bouton Import */}
      <div className="mx-auto w-full max-w-4xl mb-8 rounded-2xl border border-gray-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-900/70 backdrop-blur p-4 sm:p-5 shadow-lg">
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Ajouter un compétiteur</h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsUploadModalOpen(true)}
                className="rounded-lg"
              >
                Importer un fichier
              </Button>
            </div>
          </div>

          {/* Encart d'information sur la colonne CLUB */}
          <div className="mt-2 rounded-md border border-yellow-300/60 bg-yellow-50/80 text-yellow-900 text-xs sm:text-sm px-3 py-2">
            <strong>Important :</strong> votre fichier Excel doit contenir une
            colonne <strong>CLUB</strong> avec
            <em> le nom exact</em> du club :{" "}
            <span className="font-semibold">{clubFromUrl || "NomDuClub"}</span>.
            Si la valeur est différente ou vide, l’import ne sera pas pris en
            compte sur cette page.
          </div>
          <span>Si vous souhaitez ajouter des arbitres ou des commissaires sportifs, vous pouvez les ajouter via le formulaire ci-dessous avec la discipline Arbitre ou Commissaire Sportif.</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Nom *</label>
            <input
              name="nom_competiteur"
              value={newCompetiteur.nom_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Prénom *</label>
            <input
              name="prenom_competiteur"
              value={newCompetiteur.prenom_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Date de naissance *</label>
            <input
              type="date"
              name="ddn_competiteur"
              value={newCompetiteur.ddn_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Licence</label>
            <input
              name="licence_competiteur"
              value={newCompetiteur.licence_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Genre *</label>
            <select
              name="genre_competiteur"
              value={newCompetiteur.genre_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
            >
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Poids (optionnel)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              placeholder="ex: 24.0"
              name="poids_competiteur"
              value={newCompetiteur.poids_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Discipline</label>
            <select
              name="discipline_competiteur"
              value={newCompetiteur.discipline_competiteur}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
            >
              {(availableDisciplines.length
                ? availableDisciplines
                : ["Judo"]
              ).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          {/* Grade (sélecteur + badge) */}
          <div>
            <label className="block text-sm mb-1">Grade</label>
            <div className="flex items-center gap-2">
              <select
                name="grade_competiteur"
                value={newCompetiteur.grade_competiteur}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
              >
                <option value="">—</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 ${getGradeClasses(
                  newCompetiteur.grade_competiteur
                )}`}
              >
                {labelFromValue(newCompetiteur.grade_competiteur)}
              </span>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm mb-1">Club</label>
            <input
              name="club_competiteur"
              value={newCompetiteur.club_competiteur}
              readOnly
              className="w-full rounded-md border border-dashed border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/60 text-gray-600 dark:text-gray-300 px-3 py-2 text-sm cursor-not-allowed"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={handleAdd} className="rounded-lg">
            Ajouter
          </Button>
        </div>
      </div>

      {/* Barre outils liste */}
      <div className="mx-auto w-full max-w-5xl mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur p-3 border border-gray-200/70 dark:border-slate-700/70 shadow-sm">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Total :{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {filtered.length}
            </span>{" "}
            compétiteur{filtered.length > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="Rechercher (nom, prénom, licence)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 sm:w-80 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/70 backdrop-blur px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUploadModalOpen(true)}
              className="rounded-lg"
            >
              Importer
            </Button>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="mx-auto w-full max-w-5xl rounded-xl shadow-lg border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-700 dark:text-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">Nom</th>
              <th className="px-4 py-3 text-left">Prénom</th>
              <th className="px-4 py-3 text-left">Licence</th>
              <th className="px-4 py-3 text-left">Naissance</th>
              <th className="px-4 py-3 text-left">Genre</th>
              <th className="px-4 py-3 text-left">Poids</th>
              <th className="px-4 py-3 text-left">Discipline</th>
              <th className="px-4 py-3 text-left">Grade</th>
              <th className="px-4 py-3 text-left">Club</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr
                key={c.id}
                className="odd:bg-white even:bg-gray-50/70 dark:odd:bg-slate-900 dark:even:bg-slate-900/60"
              >
                <td className={tdClass}>{c.nom_competiteur}</td>
                <td className={tdClass}>{c.prenom_competiteur}</td>
                <td className={tdClass}>
                  {c.licence_competiteur || (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className={tdClass}>
                  {c.ddn_competiteur
                    ? new Date(c.ddn_competiteur).toLocaleDateString()
                    : "—"}
                </td>
                <td className={tdClass}>
                  <GenreTag g={c.genre_competiteur} />
                </td>
                <td className={tdClass}>
                  <WeightBadge w={c.poids_competiteur} />
                </td>
                <td className={tdClass}>{c.discipline_competiteur}</td>
                <td className={tdClass}>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 ${getGradeClasses(
                      c.grade_competiteur ?? ""
                    )}`}
                  >
                    {labelFromValue(c.grade_competiteur)}
                  </span>
                </td>
                <td
                  className={`${tdClass} italic text-gray-600 dark:text-gray-300`}
                >
                  {c.club_competiteur}
                </td>
                <td className={`${tdClass} text-center`}>
                  <div className="inline-flex gap-2">
                    <ButtonEdit
                      onClick={() => handleEdit(c)}
                      disabled={c.is_present}
                      className={
                        c.is_present ? "opacity-50 cursor-not-allowed" : ""
                      }
                    />
                    <ButtonDelete
                      onClick={() => handleDelete(c.id, c.is_present)}
                      disabled={c.is_present}
                      className={
                        c.is_present ? "opacity-50 cursor-not-allowed" : ""
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Aucun compétiteur saisi pour ce club pour l’instant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modale d’édition (réutilise la modale stylée) */}
      <EditCompetiteurModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        competiteur={competiteurToEdit}
        refreshList={() => fetchCompetiteurs(newCompetiteur.club_competiteur)}
      />

      {/* Modale d'import — réutilisée ; on passe le club fixé */}
      <UploadJudokasModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        competitionId={competitionId}
        refreshJudokas={() =>
          fetchCompetiteurs(newCompetiteur.club_competiteur)
        }
        fixedClub={clubFromUrl} // ⇐ permet d’imposer le club côté import
      />

      <ToastContainer />
    </div>
  );
};

export default CompetitionInvitationPage;
